import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { HeartHandshake, ChevronRight } from "lucide-react";
import api from "../lib/api";
import GratitudeStoriesViewer from "./partner/GratitudeStoriesViewer";

/**
 * Iter-27.69 — Carousel "Stories" style Instagram en haut du Dashboard.
 *
 * Affiche 1 avatar circulaire par asso qui a posté des remerciements. Les assos
 * avec des messages non-lus apparaissent en premier avec un anneau orange-jaune
 * gradient (style story non-vue Insta).
 *
 * Au clic → ouvre le GratitudeStoriesViewer en plein écran sur les messages
 * de l'asso choisie.
 */
export default function AssoStoriesCarousel() {
    const [stories, setStories] = useState(null);
    const [viewing, setViewing] = useState(null); // { association, items }

    const reload = async () => {
        try {
            const r = await api.get("/me/stories");
            setStories(r.data || []);
        } catch {
            setStories([]);
        }
    };

    useEffect(() => {
        reload();
    }, []);

    if (!stories || stories.length === 0) return null;

    const openStory = async (s) => {
        try {
            // Récupère TOUS les remerciements de cette asso (pour le viewer)
            const r = await api.get(`/associations/${s.association_id}/news`);
            const gratitudes = (r.data || []).filter((n) => n.category === "gratitude");
            // Récupère aussi la fiche asso (logo, primary_color, name)
            const aR = await api.get(`/associations/${s.association_id}`);
            setViewing({ items: gratitudes, association: aR.data });
        } catch {
            // fallback minimal
            setViewing({
                items: [],
                association: { name: s.name, logo_url: s.logo_url, primary_color: s.primary_color },
            });
        }
    };

    const closeStory = () => {
        setViewing(null);
        // Refresh badges (les news viewées sont maintenant marquées via API normale)
        reload();
    };

    const hasUnread = stories.some((s) => s.unread_count > 0);

    return (
        <>
            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="col-span-12 mb-2"
                data-testid="asso-stories-carousel"
            >
                <div className="flex items-center justify-between mb-3 px-1">
                    <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-shariti-yellow/20 flex items-center justify-center">
                            <HeartHandshake className="w-3.5 h-3.5 text-shariti-yellow" />
                        </div>
                        <h3 className="text-sm font-extrabold text-white tracking-tight">
                            Messages des associations
                        </h3>
                        {hasUnread && (
                            <span className="text-[10px] font-extrabold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-shariti-orange text-white animate-pulse">
                                NOUVEAU
                            </span>
                        )}
                    </div>
                </div>

                <div className="flex gap-3 overflow-x-auto scrollbar-hide pb-2 -mx-1 px-1">
                    {stories.map((s, idx) => (
                        <StoryAvatar key={s.association_id} story={s} idx={idx} onOpen={openStory} />
                    ))}
                </div>
            </motion.div>

            <AnimatePresence>
                {viewing && (
                    <GratitudeStoriesViewer
                        items={viewing.items}
                        association={viewing.association}
                        onClose={closeStory}
                    />
                )}
            </AnimatePresence>
        </>
    );
}


function StoryAvatar({ story, idx, onOpen }) {
    const hasUnread = story.unread_count > 0;
    const accent = story.primary_color || "#EE722E";
    return (
        <motion.button
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3, delay: idx * 0.05 }}
            onClick={() => onOpen(story)}
            data-testid={`asso-story-${story.association_id}`}
            className="group shrink-0 flex flex-col items-center gap-1.5 w-20 sm:w-24 focus:outline-none"
            aria-label={`${story.name} — ${story.unread_count} message(s) non lu(s)`}
        >
            {/* Ring + avatar */}
            <div className="relative">
                {/* Iter-27.70 — Anneau "ping" d'attention sur unread (animation 4s, subtile) */}
                {hasUnread && (
                    <span
                        aria-hidden="true"
                        className="absolute inset-0 rounded-full pointer-events-none story-avatar-ping"
                        style={{
                            background: `conic-gradient(from 0deg, ${accent}, #FCDB53, #EE722E, ${accent})`,
                        }}
                    />
                )}
                {/* Ring gradient si unread (style Insta story non-vue) */}
                <div
                    className={`relative w-16 h-16 sm:w-20 sm:h-20 rounded-full p-[2.5px] transition-all ${
                        hasUnread
                            ? "bg-gradient-to-br from-shariti-orange via-shariti-yellow to-shariti-orange-light group-hover:scale-105"
                            : "bg-white/15 group-hover:bg-white/25"
                    }`}
                    style={
                        hasUnread
                            ? { background: `conic-gradient(from 0deg, ${accent}, #FCDB53, #EE722E, ${accent})` }
                            : {}
                    }
                >
                    <div className="w-full h-full rounded-full bg-shariti-deep-black p-[2px]">
                        <div className="w-full h-full rounded-full bg-white p-1 flex items-center justify-center overflow-hidden">
                            {story.logo_url ? (
                                <img
                                    src={story.logo_url}
                                    alt={story.name}
                                    className="w-full h-full object-contain"
                                />
                            ) : (
                                <HeartHandshake
                                    className="w-7 h-7"
                                    style={{ color: accent }}
                                />
                            )}
                        </div>
                    </div>
                </div>
                {/* Badge count unread */}
                {hasUnread && (
                    <motion.span
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="absolute -top-0.5 -right-0.5 min-w-[20px] h-5 px-1.5 rounded-full bg-shariti-orange text-white text-[10px] font-extrabold flex items-center justify-center ring-2 ring-shariti-deep-black shadow-lg"
                    >
                        {story.unread_count}
                    </motion.span>
                )}
            </div>
            {/* Nom asso */}
            <p
                className={`text-[10px] sm:text-[11px] font-bold text-center truncate w-full leading-tight ${
                    hasUnread ? "text-white" : "text-white/55"
                }`}
            >
                {story.name}
            </p>
        </motion.button>
    );
}
